import * as React from 'react';
import i18next from 'i18next';
import { _DeepReadonlyArray } from 'utility-types/dist/mapped-types';
import { CommonRootState } from '../combineReducers';
import { emptyGuid } from '../constants/emptyGuid';
import { fetchScmConnectedClients, IScmConnectedClient } from '../slices/scmConnectedClients';
import { fetchMainSubscription } from '../slices/mainSubscription';
import { fetchOrganizationUserGroupsAndRolesIfNeeded } from '../actions/organizationUserGroupsAndRoles';
import { fetchPrimaryOrganizationReleasesIfNeeded } from '../actions/primaryOrganizationReleases';
import { fetchServerTokensIfNeeded } from '../actions/serverTokens';
import { fetchUserInfoIfNeeded } from '../actions/userInfo';
import { fetchUserOrganizationsIfNeeded } from '../actions/userOrganizations';
import { hotjar } from 'react-hotjar';
import { IReleaseStatus } from '../interfaces/release';
import { IUserOrganization } from '../interfaces/userOrganization';
import { OrganizationFeature } from '../interfaces/organization';
import { PECEnv } from '../interfaces/serverTokens';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

const { Client, PrimeContractor, Subcontractor, Subscriber } = OrganizationFeature;
const { Approved } = IReleaseStatus;

const HOTJAR_BETA_ID = 1991515;
const HOTJAR_DEV_ID = 1991514;
const HOTJAR_PROD_ID = 1991517;
const HOTJAR_SNIPPET_VERSION = 6;

const getHotjarQueue = () => {
  return function() {
    (window['hj']['q'] = window['hj']['q'] || []).push(arguments);
  };
};

const getHotjarInstance = () => {
  return (window['hj'] = window['hj'] || getHotjarQueue());
};

export const getPrimaryOrganizationType = (
  userOrganizations: _DeepReadonlyArray<IUserOrganization>,
  primaryOrganizationId: string
) => {
  if (primaryOrganizationId === emptyGuid) {
    return 'Admin';
  }
  const primaryOrganization = userOrganizations.find(o => o.id === primaryOrganizationId);

  return primaryOrganization && primaryOrganization.features.includes(Client)
    ? 'Client'
    : primaryOrganization && primaryOrganization.features.includes(Subscriber)
    ? 'Contractor'
    : 'Other';
};

/**
 * Determines the contractor type for the primary organization.
 * Returns 'Prime Contractor', 'Subcontractor', both (comma-separated), or '' for non-contractor orgs.
 * CPRO-4322: Used for Pendo metadata.
 */
export const getContractorType = (
  userOrganizations: _DeepReadonlyArray<IUserOrganization>,
  primaryOrganizationId: string
): string => {
  if (primaryOrganizationId === emptyGuid) {
    return '';
  }

  const primaryOrganization = userOrganizations.find(o => o.id === primaryOrganizationId);

  if (!primaryOrganization) {
    return '';
  }

  const { features } = primaryOrganization;
  const isPrime = features.includes(PrimeContractor);
  const isSub = features.includes(Subcontractor);

  if (isPrime && isSub) {
    return 'Prime Contractor, Subcontractor';
  }

  if (isPrime) {
    return 'Prime Contractor';
  }

  if (isSub) {
    return 'Subcontractor';
  }

  return '';
};

/**
 * Formats connected client names as a JSON stringified array for Pendo.
 * Returns '' if the organization has no contractor type or no clients.
 * CPRO-4322: Used for Pendo metadata. Format matches the existing `clients` (approvedReleases) field.
 */
export const getScmConnectedClientsString = (clients: IScmConnectedClient[], contractorType: string): string => {
  if (!contractorType) {
    return '';
  }

  if (!clients || clients.length === 0) {
    return '';
  }

  return JSON.stringify(clients.map(client => client.clientName).filter(name => name));
};

export const getComplianceRegions = (
  userOrganizations: _DeepReadonlyArray<IUserOrganization>,
  primaryOrganizationId: string
) => {
  const complianceRegions = [];
  const primaryOrganization = userOrganizations.find(o => o.id === primaryOrganizationId);

  if (primaryOrganization) {
    const { features } = primaryOrganization;

    if (features.includes(OrganizationFeature.ComplianceUSA)) complianceRegions.push('US');
    if (features.includes(OrganizationFeature.ComplianceUK)) complianceRegions.push('UK');
    if (features.includes(OrganizationFeature.ComplianceAUS)) complianceRegions.push('AU');
    if (features.includes(OrganizationFeature.ComplianceGlobal)) complianceRegions.push('Global');
  }

  return complianceRegions;
};

export const TrackerWrapper: React.FC = ({ children }) => {
  const dispatch = useDispatch();
  const { organization } = useSelector((state: CommonRootState) => state.organization);
  const { organizationUserGroupsAndRoles } = useSelector(
    (state: CommonRootState) => state.organizationUserGroupsAndRoles
  );
  const { primaryOrganizationReleases } = useSelector((state: CommonRootState) => state.primaryOrganizationReleases);
  const { userInfo } = useSelector((state: CommonRootState) => state.userInfo);
  const { userOrganizations } = useSelector((state: CommonRootState) => state.userOrganizations);
  const { serverTokens, pendoIsLoaded } = useSelector((state: CommonRootState) => state.serverTokens);
  const {
    mainSubscription: { countryISO2 = '', description = '' }
  } = useSelector((state: CommonRootState) => state.mainSubscription);
  const { clients: scmConnectedClients, isLoaded: scmConnectedClientsLoaded } = useSelector(
    (state: CommonRootState) => state.scmConnectedClients
  );

  const pendo = window['pendo'] as pendo.Pendo | undefined;

  useEffect(() => {
    const { beta, dev, production } = PECEnv;

    if (serverTokens) {
      switch (serverTokens.pecEnv) {
        case dev:
          hotjar.initialize(HOTJAR_DEV_ID, HOTJAR_SNIPPET_VERSION);
          break;
        case beta:
          hotjar.initialize(HOTJAR_BETA_ID, HOTJAR_SNIPPET_VERSION);
          break;
        case production:
          hotjar.initialize(HOTJAR_PROD_ID, HOTJAR_SNIPPET_VERSION);
          break;
        default:
          break;
      }
    }
  }, [serverTokens]);

  useEffect(() => {
    if (userInfo && getHotjarInstance()) {
      getHotjarInstance()('identify', userInfo.userId, { email: userInfo.emailAddress });
      getHotjarInstance()('identify', userInfo.userId, { user_name: userInfo.userName });
      organization && getHotjarInstance()('identify', userInfo.userId, { company_name: organization.name });
    }
  }, [userInfo, organization]);

  useEffect(() => {
    dispatch(fetchUserInfoIfNeeded());
  }, [dispatch, userInfo]);

  useEffect(() => {
    dispatch(fetchUserOrganizationsIfNeeded());
  }, [dispatch, userOrganizations]);

  useEffect(() => {
    if (userInfo) {
      const { primaryOrganizationId, userId } = userInfo;
      dispatch(fetchOrganizationUserGroupsAndRolesIfNeeded(primaryOrganizationId, userId));
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    if (userInfo) {
      const { primaryOrganizationId } = userInfo;
      dispatch(fetchPrimaryOrganizationReleasesIfNeeded(primaryOrganizationId));
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    dispatch(fetchServerTokensIfNeeded());
  }, [dispatch, serverTokens]);

  useEffect(() => {
    organization && dispatch(fetchMainSubscription(organization.id));
  }, [dispatch, organization]);

  // CPRO-4322: Fetch connected clients for contractor organizations
  useEffect(() => {
    if (userInfo && userOrganizations) {
      const contractorType = getContractorType(userOrganizations, userInfo.primaryOrganizationId);
      if (contractorType && contractorType.includes('Subcontractor')) {
        dispatch(fetchScmConnectedClients(userInfo.primaryOrganizationId));
      }
    }
  }, [dispatch, userInfo, userOrganizations]);

  useEffect(() => {
    if (
      userInfo &&
      userOrganizations &&
      serverTokens &&
      (organizationUserGroupsAndRoles || userInfo.primaryOrganizationName == '') &&
      (primaryOrganizationReleases || userInfo.primaryOrganizationName == '') &&
      // CPRO-4322: Wait for connected clients to load if this is a subcontractor org
      (scmConnectedClientsLoaded ||
        !getContractorType(userOrganizations, userInfo.primaryOrganizationId).includes('Subcontractor')) &&
      pendo &&
      pendoIsLoaded
    ) {
      if (!pendo.isReady || !pendo.isReady()) {
        const { primaryOrganizationId, primaryOrganizationName, userId } = userInfo;

        const currentRoles = JSON.stringify(
          organizationUserGroupsAndRoles ? organizationUserGroupsAndRoles.roles.map(r => r.name) : []
        );
        const organizationType = getPrimaryOrganizationType(userOrganizations, primaryOrganizationId);
        const contractorType = getContractorType(userOrganizations, primaryOrganizationId);
        const scmConnectedClientsString = getScmConnectedClientsString(scmConnectedClients, contractorType);
        const approvedReleases =
          organizationType === 'Contractor' && primaryOrganizationReleases
            ? JSON.stringify(
                primaryOrganizationReleases.filter(r => r.status === Approved).map(r => r.releasedToOrganizationName)
              )
            : '';

        pendo.initialize({
          visitor: {
            id: userId,
            product: 'CompliancePro',
            environment: serverTokens.pecEnv,
            organizationUserRoles: currentRoles,
            language: i18next.language
          },
          account: {
            id: primaryOrganizationId,
            name: primaryOrganizationName,
            organizationType,
            contractorType,
            scmConnectedClients: scmConnectedClientsString,
            clients: approvedReleases,
            complianceRegions: getComplianceRegions(userOrganizations, primaryOrganizationId),
            subscriptionRegion: countryISO2,
            mainSubscription: description
          }
        });
      }
    }
  }, [
    userInfo,
    userOrganizations,
    pendo,
    pendoIsLoaded,
    serverTokens,
    organizationUserGroupsAndRoles,
    primaryOrganizationReleases,
    scmConnectedClients,
    countryISO2,
    description,
    scmConnectedClientsLoaded
  ]);

  useEffect(() => {
    if (!pendo) return;

    const handleLanguageChanged = (language: string) => {
      if (pendo.isReady && pendo.isReady()) {
        pendo.updateOptions({ visitor: { language } });
      }
    };

    i18next.on('languageChanged', handleLanguageChanged);
    return () => {
      i18next.off('languageChanged', handleLanguageChanged);
    };
  }, [pendo]);

  return <>{children}</>;
};
